// Party (acampamento de caça): membros (vida, level, onde estão), convidar por nome, aceitar convites, sair e expulsar.
import { useState } from 'preact/hooks';
import { PARTY_MAX, partyBonusPct, type PartyMember } from '../../../../shared/src/game/party';

export interface PartyState { members: PartyMember[]; invites: Array<{ id: number; name: string }>; bonus: number }

const VOCS: Array<[string, string, string]> = [['knight', 'Knight', '🛡️'], ['paladin', 'Paladin', '🏹'], ['sorcerer', 'Sorcerer', '🔥'], ['druid', 'Druid', '🌿'], ['monk', 'Monk', '👊']];
const ICON: Record<string, string> = Object.fromEntries(VOCS.map(([id, , i]) => [id, i]));
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : '');
const TIERS = [1, 2, 3, 4];
const HELP_KEY = 'nx-help-party';
const seenHelp = () => { try { return localStorage.getItem(HELP_KEY) === '1'; } catch { return true; } };

export function Party(props: { st: PartyState | null; me: number; send: (op: 'invite' | 'accept' | 'decline' | 'leave' | 'kick', name?: string, id?: number) => void }) {
  const [name, setName] = useState('');
  const [help, setHelp] = useState(() => !seenHelp());
  const st = props.st ?? { members: [], invites: [], bonus: 0 };
  const leader = st.members.find((m) => m.leader);
  const iAmLeader = !st.members.length || leader?.id === props.me;
  const meM = st.members.find((m) => m.id === props.me);
  const vocSet = new Set(st.members.map((m) => m.vocation));
  const nVoc = vocSet.size;
  const maxLv = Math.max(0, ...st.members.map((m) => m.level));
  const minShare = Math.ceil((maxLv * 2) / 3);
  const nextBonus = nVoc < 4 ? partyBonusPct(nVoc + 1) : 0;
  const toggleHelp = () => { setHelp(!help); try { localStorage.setItem(HELP_KEY, '1'); } catch { /* sem storage */ } };
  const invite = () => { props.send('invite', name.trim()); setName(''); };

  return (
    <div class={'pt' + (st.members.length ? ' pt-on' : '')}>
      {/* cabeçalho: a fogueira do acampamento */}
      <div class="pt-head">
        <div class="pt-fire"><span>🔥</span><i /><i /><i /></div>
        <div class="pt-head-txt">
          <div class="pt-title">Acampamento de Caça <button class="pt-help" onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="pt-sub">Junte vocations diferentes ao redor da fogueira e a XP rende muito mais.</div>
          <div class="pt-tags">
            <span>Até {PARTY_MAX} membros</span>
            <span>Level mínimo: 2/3 do maior</span>
            <span>Mesma hunt para dividir</span>
          </div>
        </div>
        <div class={'pt-bonus' + (st.bonus ? ' lit' : '')} title="Bônus atual de XP compartilhada">
          <small>Bônus de XP</small>
          <b>+{st.bonus}<em>%</em></b>
          <span>{st.members.length ? `${nVoc} vocation${nVoc === 1 ? '' : 's'}` : 'sem party'}</span>
        </div>
      </div>

      {help && (
        <div class="pt-steps">
          <div><i>📯</i><b>1. Convide</b><small>O líder (ou quem ainda não tem party) convida pelo nome. O outro aceita aqui.</small></div>
          <div><i>🗺️</i><b>2. Mesma hunt</b><small>Quem caça na mesma hunt divide a experiência dos monstros.</small></div>
          <div><i>⚖️</i><b>3. Faixa de level</b><small>Para dividir, seu level precisa ser de pelo menos 2/3 do maior level presente.</small></div>
          <div><i>🎭</i><b>4. Misture vocations</b><small>1: +20% · 2: +30% · 3: +60% · 4 ou mais: +100% de bônus.</small></div>
        </div>
      )}

      {/* convites recebidos */}
      {!!st.invites.length && <div class="pt-invs">{st.invites.map((i) => (
        <div key={i.id} class="pt-inv">
          <span class="pt-inv-ico">📯</span>
          <span class="pt-inv-txt"><b>{i.name}</b> convidou você para a party.</span>
          <span class="pt-inv-btns"><button class="tb-button green" onClick={() => props.send('accept', undefined, i.id)}>Aceitar</button> <button class="tb-button" onClick={() => props.send('decline', undefined, i.id)}>Recusar</button></span>
        </div>
      ))}</div>}

      {/* trilha do bônus por vocations diferentes */}
      <div class="pt-ladder">
        <div class="pt-ladder-top">
          <span class="pt-sec">Vocations na fogueira</span>
          <span class="pt-vocs">{VOCS.map(([id, l, ic]) => <i key={id} class={vocSet.has(id) ? 'on' : ''} title={l + (vocSet.has(id) ? ' na party' : ' faltando')}>{ic}</i>)}</span>
        </div>
        <div class="pt-tiers">
          {TIERS.map((t) => (
            <div key={t} class={'pt-tier' + (nVoc >= t ? ' done' : nVoc + 1 === t ? ' next' : '')}>
              <b>+{partyBonusPct(t)}%</b><small>{t === 4 ? '4+ vocs' : `${t} voc${t > 1 ? 's' : ''}`}</small>
            </div>
          ))}
        </div>
        <div class="pt-hint">{!st.members.length ? 'Forme uma party para acender a fogueira.' : nVoc >= 4 ? 'Bônus máximo! A fogueira está no auge.' : `Traga mais uma vocation diferente para subir para +${nextBonus}%.`}</div>
      </div>

      {/* membros */}
      {st.members.length ? <>
        <div class="pt-sec">Party <small>· {st.members.length}/{PARTY_MAX} membros{maxLv ? ` · divide a partir do level ${minShare}` : ''}</small></div>
        <div class="pt-list">{st.members.map((m) => {
          const low = m.level < minShare;
          const same = !!meM && m.map === meM.map;
          return (
            <div key={m.id} class={'pt-card' + (m.leader ? ' leader' : '') + (m.id === props.me ? ' me' : '') + (low ? ' low' : '')}>
              <span class="pt-ava">{ICON[m.vocation] ?? '👤'}{m.leader && <i class="pt-crown" title="Líder">★</i>}</span>
              <div class="pt-info">
                <b>{m.name}{m.id === props.me && <em> (você)</em>}</b>
                <small>{cap(m.vocation)} · level {m.level}</small>
                <div class="pt-hp" title={`Vida: ${m.hp}%`}><i class={m.hp < 30 ? 'crit' : m.hp < 60 ? 'mid' : ''} style={{ width: m.hp + '%' }} /></div>
                <div class="pt-flags">
                  <span class={same ? 'ok' : ''} title="Onde está agora">📍 {m.map}</span>
                  {low && <span class="warn" title="Abaixo de 2/3 do maior level presente: não divide a XP">abaixo da faixa</span>}
                </div>
              </div>
              {iAmLeader && m.id !== props.me && <button class="tb-button pt-kick" onClick={() => props.send('kick', undefined, m.id)}>Expulsar</button>}
            </div>
          );
        })}</div>
        <div class="pt-leave"><button class="tb-button" onClick={() => props.send('leave')}>Sair da party</button></div>
      </> : <div class="pt-empty">🏕️ Você não está numa party. {iAmLeader ? 'Convide alguém abaixo para montar o acampamento.' : ''}</div>}

      {iAmLeader && <div class="pt-add">
        <input class="mk-search" placeholder="Nome do personagem" value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) invite(); }} />
        <button class="tb-button green" disabled={!name.trim()} onClick={invite}>Convidar</button>
      </div>}
    </div>
  );
}
